import { Service } from "@turizi/shared";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { NumberInput, Button, Select, Popover, Tooltip } from "@mantine/core";
import {
    IconUsersGroup,
    IconShoppingCart,
    IconInfinity,
    IconUsers,
    IconGauge,
    IconCircleCheck,
    IconCircleX,
} from "@tabler/icons-react";
import {
    useServiceAvailableLimits,
    useServiceBookingsLimits,
    useServiceInventory,
    useUpdateServiceInventory
} from "@/hooks/api-hooks";
import {
    formatDay,
    getLocalizedWeekDays,
    getMonthOptions,
    getDaysInMonth,
    getFirstDayOfMonth,
    formatDateKey
} from "@/lib/format-utils";
import { notify } from "@/lib/notify";

interface Props {
    service: Service;
    onChange?: (limits: { [key: string]: number }, defaultLimit?: number) => void;
}

export default function EditServiceLimits({ service, onChange }: Props) {
    const { t } = useTranslation();
    const isPaxBased = !service.price_by || ['pax', 'night_and_pax', 'pax_day', 'pax_hour'].includes(service.price_by);

    const { data: inventoryRes } = useServiceInventory(service.id);
    const updateInventory = useUpdateServiceInventory();

    const inventory = inventoryRes?.data;

    const [limits, setLimits] = useState<{ [key: string]: number }>({});
    const [defaultLimit, setDefaultLimit] = useState<number>(Infinity);
    const [tempDefaultLimit, setTempDefaultLimit] = useState<number>(Infinity);

    useEffect(() => {
        const activeInventory = inventory || service?.inventory;
        if (activeInventory) {
            setLimits(activeInventory.limits || {});
            const def = activeInventory.default_limit !== null && activeInventory.default_limit !== undefined
                ? activeInventory.default_limit
                : (service.default_limit !== null && service.default_limit !== undefined ? service.default_limit : Infinity);
            setDefaultLimit(def);
            setTempDefaultLimit(def);
        } else if (service) {
            setLimits({});
            const def = service.default_limit !== null && service.default_limit !== undefined
                ? service.default_limit
                : Infinity;
            setDefaultLimit(def);
            setTempDefaultLimit(def);
        }
    }, [inventory, service]);

    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [editingDay, setEditingDay] = useState<string | null>(null);
    const [editValue, setEditValue] = useState<number>(0);

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDayOfMonth = getFirstDayOfMonth(currentMonth);

    const startDay = formatDateKey(year, month, 1);
    const endDay = formatDateKey(year, month, daysInMonth);

    const { data: availableLimitsRes } = useServiceAvailableLimits({
        service_id: service.id,
        start_day: startDay,
        end_day: endDay
    });
    const availableDays = availableLimitsRes?.data?.days || availableLimitsRes?.data || {};

    const { data: bookingsLimitsRes } = useServiceBookingsLimits({
        service_id: service.id,
        start_day: startDay,
        end_day: endDay
    });
    const bookedDays = bookingsLimitsRes?.data?.days || bookingsLimitsRes?.data || {};

    const monthOptions = getMonthOptions();

    const handleDayClick = (dateKey: string) => {
        setEditingDay(dateKey);
        const dayVal = limits[dateKey] ?? (defaultLimit === Infinity ? 0 : defaultLimit);
        setEditValue(dayVal);
    };

    const handleSaveDay = () => {
        if (editingDay) {
            const newLimits = { ...limits };
            if (editValue === defaultLimit) {
                delete newLimits[editingDay];
            } else {
                newLimits[editingDay] = editValue;
            }
            setLimits(newLimits);
            onChange?.(newLimits, defaultLimit === Infinity ? undefined : defaultLimit);
            updateInventory.mutate({
                serviceId: service.id,
                limits: newLimits,
                default_limit: defaultLimit === Infinity ? null : defaultLimit,
            }, {
                onSuccess: () => {
                    notify.success(t('limit_update_success'), '');
                },
                onError: () => {
                    notify.error(t('limit_update_error'), '');
                }
            });
            setEditingDay(null);
        }
    };

    const handleClearDay = () => {
        if (editingDay) {
            const newLimits = { ...limits };
            delete newLimits[editingDay];
            setLimits(newLimits);
            onChange?.(newLimits, defaultLimit === Infinity ? undefined : defaultLimit);
            updateInventory.mutate({
                serviceId: service.id,
                limits: newLimits,
                default_limit: defaultLimit === Infinity ? null : defaultLimit,
            }, {
                onSuccess: () => {
                    notify.success(t('limit_update_success'), '');
                },
                onError: () => {
                    notify.error(t('limit_update_error'), '');
                }
            });
            setEditingDay(null);
        }
    };

    const handleSaveDefaultLimit = () => {
        setDefaultLimit(tempDefaultLimit);
        onChange?.(limits, tempDefaultLimit === Infinity ? undefined : tempDefaultLimit);
        updateInventory.mutate({
            serviceId: service.id,
            limits: limits,
            default_limit: tempDefaultLimit === Infinity ? null : tempDefaultLimit,
        }, {
            onSuccess: () => {
                notify.success(t('limit_update_success'), '');
            },
            onError: () => {
                notify.error(t('limit_update_error'), '');
            }
        });
    };

    const today = new Date();
    const todayKey = formatDateKey(today.getFullYear(), today.getMonth(), today.getDate());

    const weekDays = getLocalizedWeekDays();

    return (
        <div className="flex flex-col gap-4 max-w-4xl">
            <div className="bg-white border border-gray-200/80 rounded-xl p-4 sm:p-5 shadow-2xs max-w-xl">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2">
                        <div className="text-base font-bold text-gray-900">
                            {t("default_limit")}
                        </div>
                        <span className="text-[11px] font-semibold text-gray-600 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-md">
                            {t("default_value")}
                        </span>
                    </div>
                    <Tooltip label={t("limits_are_based_on_price_by")}>
                        <div className="bg-blue-50 text-blue-700 border border-blue-200/80 px-2.5 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1.5 cursor-help">
                            {isPaxBased ? <IconUsersGroup size={14} /> : <IconShoppingCart size={14} />}
                            <span className="capitalize">{service.price_by || 'pax'} / {t("day")}</span>
                        </div>
                    </Tooltip>
                </div>
                <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                    {t("default_limit_helper")}
                </p>
                <div className="flex items-center gap-2.5">
                    <NumberInput
                        value={tempDefaultLimit === Infinity ? '' : tempDefaultLimit}
                        onChange={(value) => setTempDefaultLimit(value === '' || value === undefined ? Infinity : Number(value))}
                        min={0}
                        placeholder={`∞ (${t("unlimited")})`}
                        className="w-52"
                        hideControls
                        rightSection={
                            <span className="text-xs text-gray-400 mr-2 select-none pointer-events-none">
                                {service.price_by || 'pax'}
                            </span>
                        }
                        rightSectionWidth={60}
                    />
                    <Button
                        color="black"
                        radius="xl"
                        onClick={handleSaveDefaultLimit}
                        loading={updateInventory.isPending}
                        disabled={tempDefaultLimit === defaultLimit}
                    >
                        {t("save")}
                    </Button>
                    {tempDefaultLimit !== defaultLimit && (
                        <Button
                            variant="subtle"
                            color="gray"
                            radius="xl"
                            onClick={() => setTempDefaultLimit(defaultLimit)}
                        >
                            {t("cancel")}
                        </Button>
                    )}
                </div>
            </div>

            <div className="solid-box-border">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <Select
                        value={currentMonth.toISOString()}
                        onChange={(value) => {
                            if (value) setCurrentMonth(new Date(value));
                        }}
                        data={monthOptions}
                        size="sm"
                        className="font-bold w-52"
                        placeholder={currentMonth.toLocaleDateString('default', { month: 'long', year: 'numeric' })}
                    />
                </div>
                <div className="grid grid-cols-7 gap-1">
                    {weekDays.map(day => (
                        <div key={day} className="text-center font-semibold text-sm p-2 text-gray-600">
                            {day}
                        </div>
                    ))}

                    {Array.from({ length: firstDayOfMonth }).map((_, index) => (
                        <div key={`empty-${index}`} className="p-2"></div>
                    ))}

                    {Array.from({ length: daysInMonth }).map((_, index) => {
                        const day = index + 1;
                        const dateKey = formatDateKey(year, month, day);
                        const limit = limits[dateKey] ?? defaultLimit;
                        const isUnlimited = limit === Infinity;
                        const rawAvailable = availableDays[dateKey];
                        const taken = bookedDays[dateKey] ?? 0;
                        const isToday = dateKey === todayKey;
                        const hasDifferentValue = limit !== defaultLimit;
                        const hasStoredOverride = dateKey in limits;
                        const isPast = new Date(dateKey) < new Date(todayKey);

                        const available = isUnlimited
                            ? Infinity
                            : (rawAvailable !== undefined && rawAvailable !== null ? Number(rawAvailable) : Math.max(0, limit - taken));
                        const isSoldOut = !isUnlimited && available <= 0;

                        return (
                            <Popover
                                key={dateKey}
                                opened={editingDay === dateKey}
                                onChange={(opened) => !opened && setEditingDay(null)}
                                withArrow
                                position="top"
                            >
                                <Popover.Target>
                                    <div
                                        onClick={() => handleDayClick(dateKey)}
                                        className={`
                                            relative rounded-lg p-2 cursor-pointer transition-all border flex flex-col justify-between min-h-[64px]
                                            ${isToday ? 'ring-2 ring-blue-500' : ''}
                                            ${hasDifferentValue
                                                ? 'bg-amber-50/70 border-amber-300 hover:bg-amber-100/70 hover:border-amber-400 shadow-2xs'
                                                : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                            }
                                            ${isPast ? 'opacity-50' : ''}
                                        `}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className={`text-sm ${hasDifferentValue ? 'font-bold text-amber-950' : 'font-medium text-gray-700'}`}>
                                                {day}
                                            </span>
                                        </div>

                                        <div className="mt-1.5">
                                            <div className="flex items-center justify-between text-[11px]">
                                                {/* Available places */}
                                                <div
                                                    className="flex items-center gap-1"
                                                    title={`${t("available")}: ${isUnlimited ? '∞' : available}`}
                                                >
                                                    <IconUsers size={11} className={isSoldOut ? "text-red-200" : "text-emerald-200"} />
                                                    <span className={`font-semibold ${isSoldOut ? 'text-red-600' : 'text-emerald-600'}`}>
                                                        {isUnlimited ? '∞' : available}
                                                    </span>
                                                </div>

                                                {/* Limit */}
                                                <div
                                                    className="flex items-center gap-1"
                                                    title={`${t("limit")}: ${isUnlimited ? '∞' : limit}`}
                                                >
                                                    <IconGauge
                                                        size={13}
                                                        className={hasDifferentValue ? "text-amber-200 shrink-0" : "text-gray-200 shrink-0"}
                                                    />
                                                    <span className={`font-semibold ${hasDifferentValue ? 'text-amber-900 font-bold' : 'text-gray-600'}`}>
                                                        {isUnlimited ? <IconInfinity size={11} className="inline-block" /> : limit}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Popover.Target>
                                <Popover.Dropdown>
                                    <div className="p-2.5 min-w-[220px]">
                                        <div className="text-sm font-semibold mb-2 text-gray-900">
                                            {formatDay(dateKey)}
                                        </div>

                                        <div className="grid grid-cols-3 gap-1 bg-gray-50 border border-gray-200/80 rounded-lg p-2 mb-2.5 text-center">
                                            <div>
                                                <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider flex items-center justify-center gap-1">
                                                    {isPaxBased ? <IconUsers size={11} className="text-gray-400 shrink-0" /> : <IconShoppingCart size={11} className="text-gray-400 shrink-0" />}
                                                    <span>{t("booked")}</span>
                                                </div>
                                                <div className={`text-sm font-bold mt-0.5 ${taken > 0 ? 'text-blue-600' : 'text-gray-700'}`}>
                                                    {taken}
                                                </div>
                                            </div>
                                            <div className="border-x border-gray-200">
                                                <div className="text-[10px] font-medium text-gray-500 uppercase tracking-wider flex items-center justify-center gap-1">
                                                    <IconCircleCheck size={11} className="text-emerald-600 shrink-0" />
                                                    <span>{t("available")}</span>
                                                </div>
                                                <div className={`text-sm font-bold mt-0.5 ${isSoldOut ? 'text-red-600' : 'text-emerald-600'}`}>
                                                    {isUnlimited ? '∞' : available}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wider flex items-center justify-center gap-1">
                                                    <IconGauge size={11} className="text-gray-400 shrink-0" />
                                                    <span>{t("limit")}</span>
                                                </div>
                                                <div className={`text-sm font-bold mt-0.5 ${hasDifferentValue ? 'text-amber-900' : 'text-gray-700'}`}>
                                                    {isUnlimited ? '∞' : limit}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="text-xs text-gray-500 mb-2.5 flex items-center justify-between">
                                            <span>{t("default_limit")}:</span>
                                            <span className="font-semibold text-gray-700">
                                                {defaultLimit === Infinity ? '∞' : `${defaultLimit} ${service.price_by || 'pax'}`}
                                            </span>
                                        </div>
                                        <NumberInput
                                            value={editValue}
                                            onChange={(value) => setEditValue(Number(value))}
                                            min={0}
                                            placeholder={`Max ${service.price_by || 'pax'}`}
                                            className="mb-3"
                                            rightSection={<span className="text-xs text-gray-400 mr-2 select-none pointer-events-none">{service.price_by || 'pax'}</span>}
                                            rightSectionWidth={55}
                                        />
                                        <div className="flex items-center gap-1.5 justify-end">
                                            {(hasDifferentValue || hasStoredOverride) && (
                                                <Button
                                                    size="xs"
                                                    color="red"
                                                    variant="subtle"
                                                    onClick={handleClearDay}
                                                    loading={updateInventory.isPending}
                                                >
                                                    {t("restore_default")}
                                                </Button>
                                            )}
                                            <Button
                                                size="xs"
                                                variant="subtle"
                                                color="gray"
                                                onClick={() => setEditingDay(null)}
                                            >
                                                {t("cancel")}
                                            </Button>
                                            <Button
                                                size="xs"
                                                color="black"
                                                onClick={handleSaveDay}
                                                loading={updateInventory.isPending}
                                            >
                                                {t("save")}
                                            </Button>
                                        </div>
                                    </div>
                                </Popover.Dropdown>
                            </Popover>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}